import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

const handledProductSelectEvents = new WeakSet();
const gallerySectionStates = new WeakMap();
const warmedGallerySections = new WeakSet();

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.#warmCachedVariantImages(target);
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
    if (handledProductSelectEvents.has(event)) return;
    handledProductSelectEvents.add(event);

    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    const sectionState = gallerySectionStates.get(section) ?? { sequence: 0, preloads: [] };
    gallerySectionStates.set(section, sectionState);
    const selectionSequence = ++sectionState.sequence;
    let visibleGallery = this;

    const cachedGallery = this.#getCachedVariantGallery(section, event.detail?.variantId);
    if (cachedGallery) {
      cachedGallery.setAttribute('aria-busy', 'true');
      this.replaceWith(cachedGallery);
      visibleGallery = cachedGallery;
    } else {
      this.setAttribute('aria-busy', 'true');
    }

    event.promise
      .then(async ({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        await this.#preloadPrimaryImage(newMediaGallery);

        if (selectionSequence !== sectionState.sequence || !visibleGallery.isConnected) return;
        visibleGallery.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      })
      .finally(() => {
        if (selectionSequence === sectionState.sequence && visibleGallery.isConnected) {
          visibleGallery.removeAttribute('aria-busy');
        }
      });
  };

  /**
   * Returns a locally rendered gallery for the selected variant, avoiding the product HTML round trip.
   *
   * @param {Element} section
   * @param {string | undefined} variantId
   * @returns {Element | null}
   */
  #getCachedVariantGallery(section, variantId) {
    if (!variantId) return null;

    const template = Array.from(section.querySelectorAll('template[data-cf-variant-gallery-ids]')).find((candidate) =>
      candidate.dataset.cfVariantGalleryIds?.split(/\s+/).includes(variantId)
    );
    const gallery = template?.content?.querySelector('media-gallery');
    return gallery instanceof Element ? gallery.cloneNode(true) : null;
  }

  /**
   * Warms only the primary image for a small number of variant galleries during idle time.
   * Images inside template elements do not download until cloned, so this keeps the initial page light.
   *
   * @param {Element | null} section
   */
  #warmCachedVariantImages(section) {
    if (!section || warmedGallerySections.has(section)) return;
    warmedGallerySections.add(section);

    const warm = () => {
      if (globalThis.navigator?.connection?.saveData) return;
      if (typeof section.querySelectorAll !== 'function') return;

      const state = gallerySectionStates.get(section) ?? { sequence: 0, preloads: [] };
      gallerySectionStates.set(section, state);
      const seenSources = new Set();

      for (const template of Array.from(section.querySelectorAll('template[data-cf-variant-gallery-ids]')).slice(0, 6)) {
        const image = template.content?.querySelector('slideshow-slide:first-of-type img.product-media__image');
        if (!(image instanceof HTMLImageElement) || seenSources.has(image.src)) continue;

        seenSources.add(image.src);
        const preloadImage = new Image();
        preloadImage.decoding = 'async';
        preloadImage.fetchPriority = 'low';
        preloadImage.sizes = image.sizes || '100vw';
        preloadImage.srcset = image.srcset;
        preloadImage.src = image.src;
        state.preloads.push(preloadImage);
      }
    };

    if ('requestIdleCallback' in globalThis) {
      globalThis.requestIdleCallback(warm, { timeout: 1500 });
    } else {
      globalThis.setTimeout(warm, 500);
    }
  }

  /**
   * Preloads and decodes the next variant's primary image while the current gallery remains visible.
   *
   * @param {Element} gallery - The replacement media gallery.
   * @returns {Promise<void>}
   */
  #preloadPrimaryImage(gallery) {
    const primaryImage = gallery.querySelector('slideshow-slide:first-of-type img.product-media__image');
    if (!(primaryImage instanceof HTMLImageElement)) return Promise.resolve();

    const preloadImage = new Image();
    preloadImage.decoding = 'async';
    preloadImage.fetchPriority = 'high';

    return new Promise((resolve) => {
      let settled = false;
      const finish = () => {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        resolve();
      };
      const timeoutId = setTimeout(finish, 8000);

      preloadImage.onload = finish;
      preloadImage.onerror = finish;
      preloadImage.sizes = primaryImage.sizes || '100vw';
      preloadImage.srcset = primaryImage.srcset;
      preloadImage.src = primaryImage.src;

      if (typeof preloadImage.decode === 'function') {
        preloadImage.decode().then(finish, finish);
      }
    });
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
